'use client';

import React from 'react';
import { RotateCcw } from 'lucide-react';
import { FilterState } from './ContactsListContent';
import { Contact } from './mockContacts';

interface Props {
  filters: FilterState;
  onChange: (key: keyof FilterState, value: string) => void;
  onClear: () => void;
  contacts: Contact[];
}

const stages = [
  'New Lead', 'Identified', 'Researching', 'LinkedIn Requested', 'LinkedIn Connected',
  'Intro Sent', 'TL Shared', 'Meeting Requested', 'Meeting Confirmed',
  'Met at Event', 'Follow-up Sent', 'Opportunity Created', 'Closed / No Response', 'Not Relevant',
];

const linkedinStatuses = [
  'Not Attempted', 'Connection Planned', 'Request Sent', 'Connected',
  'Message Sent', 'Replied', 'No Response', 'Follow-up Needed',
];

const meetingStatuses = ['None', 'Requested', 'Tentative', 'Confirmed', 'Completed', 'Cancelled', 'No-show'];

export default function ContactsFilterPanel({ filters, onChange, onClear, contacts }: Props) {
  const events = Array.from(new Set(contacts.map((c) => c.event)));
  const industries = Array.from(new Set(contacts.map((c) => c.industry)));
  const hasActiveFilters = Object.entries(filters).some(([k, v]) => k !== 'search' && v !== '');

  return (
    <aside className="w-56 shrink-0 border-r border-border bg-white flex flex-col h-full overflow-y-auto scrollbar-thin">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-white z-10">
        <span className="text-xs font-semibold text-foreground">Filters</span>
        {hasActiveFilters && (
          <button
            onClick={onClear}
            className="flex items-center gap-1 text-[10px] text-red-600 hover:text-red-700 font-medium"
          >
            <RotateCcw size={10} />
            Clear all
          </button>
        )}
      </div>

      <div className="px-4 py-3 space-y-4">
        {/* Event */}
        <FilterSection label="Event">
          {events.map((ev) => (
            <FilterChip
              key={`fev-${ev}`}
              label={ev}
              active={filters.event === ev}
              onClick={() => onChange('event', filters.event === ev ? '' : ev)}
            />
          ))}
        </FilterSection>

        {/* Priority */}
        <FilterSection label="Priority">
          {(['High', 'Medium', 'Low'] as const).map((p) => (
            <FilterChip
              key={`fp-${p}`}
              label={p}
              active={filters.priority === p}
              onClick={() => onChange('priority', filters.priority === p ? '' : p)}
              colorClass={p === 'High' ? 'text-red-700 bg-red-50' : p === 'Medium' ? 'text-amber-700 bg-amber-50' : 'text-slate-600 bg-slate-100'}
            />
          ))}
        </FilterSection>

        {/* Outreach Stage */}
        <FilterSection label="Outreach Stage">
          <select
            value={filters.stage}
            onChange={(e) => onChange('stage', e.target.value)}
            className="input-base text-[11px] h-8 py-1"
          >
            <option value="">All stages</option>
            {stages.map((s) => (
              <option key={`fst-${s}`} value={s}>{s}</option>
            ))}
          </select>
        </FilterSection>

        {/* LinkedIn Status */}
        <FilterSection label="LinkedIn Status">
          <select
            value={filters.linkedinStatus}
            onChange={(e) => onChange('linkedinStatus', e.target.value)}
            className="input-base text-[11px] h-8 py-1"
          >
            <option value="">All statuses</option>
            {linkedinStatuses.map((s) => (
              <option key={`fli-${s}`} value={s}>{s}</option>
            ))}
          </select>
        </FilterSection>

        {/* Meeting Status */}
        <FilterSection label="Meeting Status">
          <select
            value={filters.meetingStatus}
            onChange={(e) => onChange('meetingStatus', e.target.value)}
            className="input-base text-[11px] h-8 py-1"
          >
            <option value="">All statuses</option>
            {meetingStatuses.map((s) => (
              <option key={`fms-${s}`} value={s}>{s}</option>
            ))}
          </select>
        </FilterSection>

        {/* Industry */}
        <FilterSection label="Industry">
          <select
            value={filters.industry}
            onChange={(e) => onChange('industry', e.target.value)}
            className="input-base text-[11px] h-8 py-1"
          >
            <option value="">All industries</option>
            {industries.map((i) => (
              <option key={`find-${i}`} value={i}>{i}</option>
            ))}
          </select>
        </FilterSection>
      </div>
    </aside>
  );
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">{label}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function FilterChip({
  label, active, onClick, colorClass,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  colorClass?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-all duration-150 ${
        active
          ? colorClass
            ? `${colorClass} border-current`
            : 'bg-primary/10 text-primary border-primary/30' :'bg-white text-muted-foreground border-border hover:bg-muted'
      }`}
    >
      {label}
    </button>
  );
}